'use client';

import React, { useEffect } from 'react';
import { SynthProvider } from '@/contexts/SynthContext';
import { useAuthStore } from '@/stores/authStore';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { useUIModalStore } from '@/stores/uiModalStore';
import { AuthModal } from '@/components/Auth/AuthModal';
import SignupBonusToast from '@/components/Auth/SignupBonusToast';
import PricingModal from '@/components/Subscription/PricingModal';
import BillingRecoveryBanner from '@/components/Billing/BillingRecoveryBanner';
import { initTheme } from '@/utils/theme';
import { installDomTranslationGuard } from '@/utils/domTranslationGuard';
import { useSignedInVisit } from '@/hooks/useSignedInVisit';

export default function Providers({ children }: { children: React.ReactNode }) {
  useSignedInVisit();
  useEffect(() => {
    initTheme();
    installDomTranslationGuard();
  }, []);

  useEffect(() => {
    useAuthStore.getState().initialize();
  }, []);

  useEffect(() => {
    return useAuthStore.subscribe((state, prev) => {
      if (state.user && state.user.id !== prev.user?.id) {
        useSubscriptionStore.getState().fetchSubscription(state.user.id);
      } else if (!state.user && prev.user) {
        useSubscriptionStore.getState().reset();
      }
    });
  }, []);

  const authModalOpen = useUIModalStore((s) => s.authModalOpen);
  const authModalContext = useUIModalStore((s) => s.authModalContext);
  const pricingModalOpen = useUIModalStore((s) => s.pricingModalOpen);
  const closeAuthModal = useUIModalStore((s) => s.closeAuthModal);
  const closePricingModal = useUIModalStore((s) => s.closePricingModal);

  return (
    <SynthProvider>
      {children}
      {/* contextMessage: production /editor and /loop-forge are served through
          THIS host (not src/layouts/Layout.tsx) — without passing the context
          the save-hook/quota signup prompts lose their framing entirely. */}
      <AuthModal isOpen={authModalOpen} onClose={closeAuthModal} contextMessage={authModalContext ?? undefined} />
      {/* E2E 2026-07-06: the bonus toast was only mounted in the react-router
          Layout, but the main signup path (editor quota prompts) runs under
          THIS Next host — without this mount the gift lands silently. */}
      <SignupBonusToast />
      <BillingRecoveryBanner />
      <PricingModal isOpen={pricingModalOpen} onClose={closePricingModal} />
    </SynthProvider>
  );
}
